// 全站同一套時間顯示（SPEC #vms-web-973「多語與時間格式」、UX-12 / UX-17；VMS UX 評審 2026-10-13 B-31）：
//   - 完整時間一律 `2026-10-13 22:34`（24 小時制、到分；要到秒的地方加 `:40`），不用各語系預設格式（「下午10:34:40」、`10/13/2026`）
//   - 清單用相對時間（「3 分鐘前」），滑過看完整時間（components/Time.tsx）
type T = (k: string, o?: Record<string, unknown>) => string;

function parts(ms: number, tz?: string): Record<string, string> {
  const f = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  const out: Record<string, string> = {};
  for (const p of f.formatToParts(new Date(ms))) out[p.type] = p.value;
  return out;
}

/** `2026-10-13 22:34`（seconds：`2026-10-13 22:34:40`）；tz 沒給 = 瀏覽器時區。讀不懂的值回空字串。 */
export function stamp(v: number | string | Date, opts: { tz?: string; seconds?: boolean } = {}): string {
  const ms = typeof v === 'number' ? v : typeof v === 'string' ? Date.parse(v) : v.getTime();
  if (!Number.isFinite(ms)) return '';
  let p: Record<string, string>;
  try {
    p = parts(ms, opts.tz);
  } catch {
    p = parts(ms);
  }
  const hh = p.hour === '24' ? '00' : p.hour;
  return `${p.year}-${p.month}-${p.day} ${hh}:${p.minute}${opts.seconds ? ':' + p.second : ''}`;
}

/** 只要日期：`2026-10-13`（到期日、加入日這類）。 */
export function dateStamp(v: number | string | Date, tz?: string): string {
  return stamp(v, { tz }).slice(0, 10);
}

/** 只要時間：`22:34:40`（同一天的清單、即時快照時間）；24 小時制。 */
export function timeStamp(v: number | string | Date, tz?: string, seconds = true): string {
  return stamp(v, { tz, seconds }).slice(11);
}

/** 相對時間（剛剛 / N 分鐘前 / N 小時前 / N 天前）；超過 7 天回完整時間。 */
export function relative(t: T, v: number | string, now = Date.now()): string {
  const ms = typeof v === 'number' ? v : Date.parse(v);
  if (!Number.isFinite(ms)) return '';
  const s = Math.round((now - ms) / 1000);
  if (s < 45) return t('time.just_now');
  const m = Math.round(s / 60);
  if (m < 60) return t('time.min_ago', { count: m });
  const h = Math.round(m / 60);
  if (h < 24) return t('time.hour_ago', { count: h });
  const d = Math.round(h / 24);
  if (d <= 7) return t('time.day_ago', { count: d });
  return stamp(ms);
}

/** 鈴鐺之類的短時間（第六輪 B6-04）：跟「現在」同一天只寫「16:07」，不同天才寫完整的「2026-10-13 16:07」。 */
export function shortStamp(v: number | string | Date, now: number = Date.now(), opts: { tz?: string } = {}): string {
  const full = stamp(v, opts);
  const today = stamp(now, opts);
  return full && full.slice(0, 10) === today.slice(0, 10) ? full.slice(11) : full;
}

/**
 * 時段（匯出工作 / 分享連結的「影片」欄；SPEC #vms-web-973「多語與時間格式」、app/vms#20）：
 * 同一天 `2026-10-16 10:00–10:10`；起訖落在同一分鐘兩端都補秒 `2026-10-16 10:00:05–10:00:35`；
 * 跨日兩端都帶日期 `2026-10-16 23:50 – 2026-10-17 00:10`。讀不懂的值回空字串。
 */
export function rangeStamp(from: number | string | Date, to: number | string | Date, tz?: string): string {
  const a = stamp(from, { tz });
  const b = stamp(to, { tz });
  if (!a || !b) return '';
  if (a.slice(0, 10) !== b.slice(0, 10)) return `${a} – ${b}`;
  if (a === b) return `${stamp(from, { tz, seconds: true })}–${stamp(to, { tz, seconds: true }).slice(11)}`;
  return `${a}–${b.slice(11)}`;
}

/**
 * 合併列「最早」那次的寫法（第七輪 B7-03）：跟最新那次同一天只寫 `16:08`；不同天寫 `10-12 16:08`（不同年才寫年）。
 * 同一分鐘回空字串（呼叫端只寫「× N 次」）。
 */
export function sinceStamp(since: number | string | Date, head: number | string | Date, tz?: string): string {
  const s = stamp(since, { tz });
  const h = stamp(head, { tz });
  if (!s) return '';
  if (s === h) return '';
  if (s.slice(0, 10) === h.slice(0, 10)) return s.slice(11);
  if (s.slice(0, 4) === h.slice(0, 4)) return s.slice(5);
  return s;
}

/** 跟參考時間同一天只寫 `16:44`，不同天寫 `10-12 16:44`，不同年寫完整（多路同步「下一段」，第七輪 B7-06）。 */
export function nearStamp(v: number | string | Date, ref: number | string | Date, tz?: string): string {
  const s = stamp(v, { tz });
  const r = stamp(ref, { tz });
  if (!s) return '';
  if (s.slice(0, 10) === r.slice(0, 10)) return s.slice(11);
  if (s.slice(0, 4) === r.slice(0, 4)) return s.slice(5);
  return s;
}
